import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, {useState} from 'react';
import {IoCloseCircleOutline} from 'react-icons/io5';
import FirestoreManager from '../../api/firebaseManager';
import '../../App.css';
import {useAuth} from '../../hooks/use-auth';
import {useBridge} from '../../hooks/use-bridge';
import {Modal} from '../modal/modal';

export const BridgeFTLinkModal = ({active, setActive}) => {
  const {
    state: {profile},
  } = useAuth();

  const [index, setIndex] = useState(0);
  const [form, setForm] = useState({
    uid: profile.uid,
    customerID: profile.customerID,
    entity: '',
    custodian_mappings: [
      {
        advisor_code: '',
        custodian: '',
        custodian_code: '',
      },
    ],
    account_mappings: [],
    error: null,
  });

  return (
    <Modal active={active} setActive={setActive} disabled_dismiss={true}>
      <div className='box flex-column'>
        {index === 0 && (
          <AddeparCredentials
            form={form}
            setForm={setForm}
            setIndex={setIndex}
            setActive={setActive}
          />
        )}
        {index === 1 && (
          <AccountMapping
            form={form}
            setForm={setForm}
            setIndex={setIndex}
            setActive={setActive}
          />
        )}
        {index === 2 && (
          <WhatIsAdvisorCode setIndex={setIndex} setActive={setActive} />
        )}
      </div>
    </Modal>
  );
};

const AddeparCredentials = ({form, setForm, setIndex, setActive}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Function to validate credentials
  const validateCredentials = () => {
    return form.entity;
  };

  // Function to handle next step
  const handleNext = () => {
    if (!validateCredentials()) {
      setError('Please fill out entity before proceeding');
      return;
    }
    setIndex(1);
    setLoading(false);
  };

  return (
    <div>
      <IconButton
        aria-label='close'
        onClick={() => setActive(false)}
        style={{position: 'absolute', top: 8, right: 8}}
      >
        <IoCloseCircleOutline />
      </IconButton>

      <Typography sx={{textAlign: 'center', fontSize: '22px'}}>
        Forest Entity
      </Typography>

      {error && (
        <Typography sx={{textAlign: 'center', color: 'red', marginTop: 2}}>
          {error}
        </Typography>
      )}
      <div className='box'>
        <Typography sx={{}}>
          Enter the Forest Entity name in the field below. This name will be
          associated with all accounts in this connection.
        </Typography>

        <FormControl fullWidth>
          <TextField
            id='Entity'
            label='Entity'
            placeholder='Entity'
            value={form.entity}
            type='text'
            onChange={evt => {
              setForm({...form, entity: evt.target.value});
              setError(''); // Clear error when user makes changes
            }}
            variant='filled'
          />
        </FormControl>

        <Typography sx={{marginTop: '16px'}}>
          After the connection is made, users can change the entity name for
          specific accounts in the More Info screen. Users will need to make
          these changes if the account owner is using a "Doing Business As"
          alias, or has an account for an LLC, and has a separate Forest entity
          for those accounts.
        </Typography>
        <Typography sx={{marginTop: '16px'}}>
          Contact support@forestsystems.com if you have any questions
        </Typography>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={() => setActive(false)}
          style={{
            zIndex: 1,
            backgroundColor: '#c5c7c6',
            height: '30px',
            border: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 32px',
            margin: '16px 32px 0px 32px',
            color: 'black',
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={loading}
          style={{
            zIndex: 1,
            backgroundColor: '#c5c7c6',
            height: '30px',
            border: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 32px',
            margin: '16px 32px 0px 32px',
            color: 'black',
          }}
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const AccountMapping = ({form, setForm, setIndex, setActive}) => {
  const {createConnection} = useBridge();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeValidationError, setCodeValidationError] = useState('');

  // Custodian options with display (label) and saved code (value)
  const custodianOptions = [
    {label: 'Apex Fintech Solutions', value: 'APX'},
    {label: 'DST Systems', value: 'DST'},
    {label: 'Fidelity 401K', value: 'FPR'},
    {label: 'Interactive Brokers', value: 'IBK'},
    {label: 'Fidelity Investments', value: 'NFS'},
    {label: 'Northern Trust', value: 'NFS'},
    {label: 'Pershing', value: 'PER'},
    {label: 'Charles Schwab', value: 'SWB'},
    {label: 'Eaglebrook', value: 'EGB'},
    {label: 'Millennium Trust', value: 'MLT'},
    {label: 'TIAA Financial Services', value: 'TIA'},
    {label: 'Pontera', value: 'PTA'},
  ];

  // Validation function for advisor codes based on custodian
  const validateAdvisorCode = (code, custodianCode) => {
    if (!code || !custodianCode) return {valid: true, error: ''};

    // Remove hyphens for validation (Schwab allows them)
    const codeWithoutHyphens = code.replace(/-/g, '');

    switch (custodianCode) {
      case 'SWB': // Charles Schwab
        // Must be 8, 9, or 10 digits, can start with 0, 00, or 000
        // Hyphen allowed between 4 digits for 8-digit codes: 0123-4567
        if (
          codeWithoutHyphens.length < 8 ||
          codeWithoutHyphens.length > 10 ||
          !/^\d+$/.test(codeWithoutHyphens)
        ) {
          return {
            valid: false,
            error:
              'Schwab codes must be 8-10 digits (e.g., 01234567, 0123-4567, 001234567, 0008336825)',
          };
        }
        // Check hyphen placement if present (only valid for 8-digit codes)
        if (code.includes('-')) {
          const parts = code.split('-');
          if (
            parts.length !== 2 ||
            parts[0].length !== 4 ||
            parts[1].length !== 4 ||
            codeWithoutHyphens.length !== 8
          ) {
            return {
              valid: false,
              error:
                'Schwab codes with hyphens must be 8 digits in format: 0123-4567',
            };
          }
        }
        break;

      case 'NFS': // Fidelity Investments
      case 'FPR': // Fidelity 401K
        // 9 characters: G followed by 8 digits, or 9 digit numeric starting with 0
        const fidelityPattern = /^(G\d{8}|0\d{8})$/;
        if (!fidelityPattern.test(code)) {
          return {
            valid: false,
            error:
              'Fidelity codes must be 9 characters: G12345678 or 012345678',
          };
        }
        break;

      case 'PER': // Pershing
        // 3 characters: numeric, alphabetical, or alphanumeric
        const pershingPattern = /^[A-Za-z0-9]{3}$/;
        if (!pershingPattern.test(code)) {
          return {
            valid: false,
            error: 'Pershing codes must be exactly 3 characters (alphanumeric)',
          };
        }
        break;

      case 'PTA': // Pontera
        // 8 digit number (Pontera User ID)
        const ponteraPattern = /^\d{8}$/;
        if (!ponteraPattern.test(code)) {
          return {
            valid: false,
            error: 'Pontera codes must be an 8-digit number (Pontera User ID)',
          };
        }
        break;

      case 'APX': // Apex Fintech Solutions
      case 'DST': // DST Systems
      case 'IBK': // Interactive Brokers
      case 'EGB': // Eaglebrook
      case 'MLT': // Millennium Trust
      case 'TIA': // TIAA Financial Services
        // For other custodians, must match exactly as provided
        // Only allow letters A-Z (upper/lower), numbers 0-9, and _ or -
        const generalPattern = /^[A-Za-z0-9_-]+$/;
        if (!generalPattern.test(code)) {
          return {
            valid: false,
            error:
              'Code can only contain letters (A-Z), numbers (0-9), underscores (_), and hyphens (-)',
          };
        }
        break;

      default:
        return {valid: true, error: ''};
    }

    return {valid: true, error: ''};
  };

  // Only the first custodian mapping is editable in this modal
  const currentCustodianMapping = form.custodian_mappings[0] || {
    advisor_code: '',
    custodian: '',
    custodian_code: '',
  };

  // Update the first custodian mapping only
  const updateCustodianMapping = (field, value) => {
    let updatedFirst = {
      ...currentCustodianMapping,
      [field]: value,
    };

    // When custodian changes, store both label and code
    if (field === 'custodian') {
      const custodianCode = value?.value || '';
      const custodianLabel = value?.label || '';
      updatedFirst = {
        ...updatedFirst,
        custodian: custodianLabel,
        custodian_code: custodianCode, // Store the code for API calls
      };
      setCodeValidationError('');
      // Re-validate advisor code if it exists
      if (currentCustodianMapping.advisor_code && custodianCode) {
        const validation = validateAdvisorCode(
          currentCustodianMapping.advisor_code,
          custodianCode,
        );
        setCodeValidationError(validation.error);
      }
    }

    // Validate advisor code if custodian is selected
    if (field === 'advisor_code') {
      const custodianCode =
        updatedFirst.custodian_code || currentCustodianMapping.custodian_code;
      if (custodianCode) {
        const validation = validateAdvisorCode(value, custodianCode);
        setCodeValidationError(validation.error);
      }
    }

    const rest =
      form.custodian_mappings && form.custodian_mappings.length > 1
        ? form.custodian_mappings.slice(1)
        : [];

    setForm({
      ...form,
      custodian_mappings: [updatedFirst, ...rest],
    });
    setError('');
  };

  // Function to handle completion
  const handleDone = async () => {
    // Validate all required fields
    if (
      !form.entity ||
      !currentCustodianMapping.custodian ||
      !currentCustodianMapping.advisor_code
    ) {
      setError('Please fill out all fields before submitting');
      return;
    }

    // Validate advisor code format
    const custodianCode = currentCustodianMapping.custodian_code;
    if (!custodianCode) {
      setError('Please select a valid custodian');
      return;
    }

    const validation = validateAdvisorCode(
      currentCustodianMapping.advisor_code,
      custodianCode,
    );
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setLoading(true);
    setError('');
    setCodeValidationError('');

    try {
      const connectResponse = await FirestoreManager.connectBridgeCustodian({
        code: currentCustodianMapping.advisor_code,
        source: custodianCode, // Use the custodian code, not the display name
        firm_id: 1295,
      });

      if (!connectResponse.success) {
        setError(
          connectResponse.error ||
            'Error connecting to BridgeFT, try again later',
        );
        setLoading(false);
        return;
      }

      // Extract the response data (excluding success/error fields)
      const {success: _, error: __, data: bridgeResponseData} = connectResponse;

      // Use the code from the response instead of user input
      const advisorCode =
        bridgeResponseData.code || currentCustodianMapping.advisor_code;

      const response = await FirestoreManager.getBridgeAccounts({
        advisor_codes: [advisorCode],
        custodian: custodianCode, // Use the custodian code, not the display name
      });

      const {success, data} = response;

      if (!success || !data || !Array.isArray(data)) {
        setError('Error finding accounts');
        setLoading(false);
        return;
      }

      // Map database accounts and add advisor_code and entity to each
      const mappedAccounts = data.map(account => {
        const {id, display_name, display_number, acct_type, custodian} =
          account;
        return {
          id,
          display_name,
          display_number,
          acct_type,
          custodian,
          advisor_code: advisorCode,
          entity: form.entity,
          ignore: false,
        };
      });

      // Update custodian_mappings with all response data from BridgeFT
      const updatedCustodianMapping = {
        ...currentCustodianMapping,
        advisor_code: advisorCode,
        ...bridgeResponseData, // includes: code, created_at_utc, firm_id, id, object, source, updated_at_utc
      };

      const connection = {
        ...form,
        custodian_mappings: [
          updatedCustodianMapping,
          ...form.custodian_mappings.slice(1),
        ],
        account_mappings: mappedAccounts,
      };

      setForm(connection);

      await createConnection(connection);

      setIndex(0);
      setActive(false);
    } catch (e) {
      setError('Error finding accounts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <IconButton
        aria-label='close'
        onClick={() => setActive(false)}
        style={{position: 'absolute', top: 8, right: 8}}
      >
        <IoCloseCircleOutline />
      </IconButton>

      <Typography sx={{textAlign: 'center', fontSize: '22px'}}>
        Advisor Code Submission
      </Typography>
      <Typography sx={{textAlign: 'center'}}>
        Enter the following information to receive transactions from BridgeFT.
      </Typography>
      {error && (
        <Typography sx={{textAlign: 'center', color: 'red', marginTop: 2}}>
          {error}
        </Typography>
      )}
      <div className='box'>
        <Grid container spacing={0}>
          <Grid
            item
            xs={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography>Select Custodian:</Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Autocomplete
                options={custodianOptions}
                value={
                  custodianOptions.find(
                    option =>
                      option.value === currentCustodianMapping.custodian_code,
                  ) || null
                }
                onChange={(event, newValue) => {
                  updateCustodianMapping('custodian', newValue || null);
                }}
                getOptionLabel={option => option?.label || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Select custodian...'
                    variant='filled'
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Typography sx={{marginTop: '16px', marginBottom: '16px'}}>
            Contact{' '}
            <a
              href='mailto:support@forestsystems.com'
              style={{
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              support@forestsystems.com
            </a>{' '}
            if you do not see your custodian.
          </Typography>

          <Grid container spacing={0}>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography>Enter Advisor Code:</Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <TextField
                  id='Code'
                  label='Code'
                  placeholder='Code'
                  value={currentCustodianMapping.advisor_code || ''}
                  type='text'
                  onChange={evt =>
                    updateCustodianMapping('advisor_code', evt.target.value)
                  }
                  variant='filled'
                  error={!!codeValidationError}
                  helperText={codeValidationError}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Typography sx={{marginTop: '16px', marginBottom: '16px'}}>
            Click{' '}
            <span
              style={{
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => setIndex(2)}
            >
              here
            </span>{' '}
            to learn what an advisor code is.
          </Typography>
        </Grid>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={() => setIndex(0)}
          style={{
            zIndex: 1,
            backgroundColor: '#c5c7c6',
            height: '30px',
            border: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 32px',
            margin: '16px 32px 0px 32px',
            color: 'black',
          }}
        >
          Back
        </Button>

        <Button
          disabled={
            loading ||
            !form.entity ||
            !currentCustodianMapping.custodian ||
            !currentCustodianMapping.advisor_code ||
            !!codeValidationError
          }
          style={{
            zIndex: 1,
            backgroundColor: '#c5c7c6',
            height: '30px',
            border: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 32px',
            margin: '16px 32px 0px 32px',
            color: 'black',
          }}
          onClick={handleDone}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

const WhatIsAdvisorCode = ({setIndex, setActive}) => {
  return (
    <div>
      <IconButton
        aria-label='close'
        onClick={() => setActive(false)}
        style={{position: 'absolute', top: 8, right: 8}}
      >
        <IoCloseCircleOutline />
      </IconButton>

      <Typography sx={{textAlign: 'center', fontSize: '22px'}}>
        What is a Advisor Code?
      </Typography>

      <div className='box'>
        <Typography sx={{marginTop: '16px'}}>
          An advisor code is a unique identifier within a custodian often
          referred to as Master/G-Number/Rep Code/Branch Code/etc. depending on
          the custodian. Accounts are associated with one or more advisor codes
          within the custodian.
        </Typography>
        <Typography sx={{marginTop: '16px'}}>
          Contact your custodian and ask for your Advisor Code.
        </Typography>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={() => setIndex(1)}
          style={{
            zIndex: 1,
            backgroundColor: '#c5c7c6',
            height: '30px',
            border: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 32px',
            margin: '16px 32px 0px 32px',
            color: 'black',
          }}
        >
          Back
        </Button>
      </div>
    </div>
  );
};
